import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const MENDOZA_FALLBACK = [-32.8908, -68.8272];

const pointIcon = L.divIcon({
  className: "",
  iconSize: [26, 30],
  iconAnchor: [13, 28],
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:#BF3437;border:3px solid #FFFBF4;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,0.4);"></div>`,
});

function ClickHandler({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

// Recentra el mapa cuando la posición cambia desde AFUERA del mapa (GPS, tipeo a mano).
function ExternalRecenter({ position }) {
  const map = useMap();
  const lastKeyRef = useRef(null);
  useEffect(() => {
    if (!position) return;
    const key = `${position[0].toFixed(6)},${position[1].toFixed(6)}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;
    map.setView(position, Math.max(map.getZoom(), 15));
  }, [position, map]);
  return null;
}

export function PointLocationPicker({ latitude, longitude, radiusMeters, onPick, existingPoints = [] }) {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const position = hasCoords ? [lat, lng] : null;

  const fallbackCenter = useMemo(() => {
    const withCoords = (existingPoints || []).filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));
    if (!withCoords.length) return null;
    const avgLat = withCoords.reduce((s, p) => s + p.latitude, 0) / withCoords.length;
    const avgLng = withCoords.reduce((s, p) => s + p.longitude, 0) / withCoords.length;
    return [avgLat, avgLng];
  }, [existingPoints]);

  const [initialCenter] = useState(() => position || fallbackCenter || MENDOZA_FALLBACK);
  const [gpsCenter, setGpsCenter] = useState(null);

  useEffect(() => {
    // Sin coordenadas propias ni puntos existentes de referencia: intentar centrar en el GPS del dispositivo.
    if (position || fallbackCenter) return;
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGpsCenter([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 6000, maximumAge: 60000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const radius = Math.max(5, parseInt(radiusMeters, 10) || 50);

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 6 }}>Ubicar en el mapa (tocá o arrastrá el marcador)</label>
      <div style={{ height: 260, borderRadius: 10, overflow: "hidden", border: "1px solid var(--invict-bone-3)" }} data-testid="point-map-picker">
        <MapContainer center={initialCenter} zoom={position ? 17 : (fallbackCenter ? 16 : 13)} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickHandler onClick={onPick} />
          <ExternalRecenter position={position || gpsCenter} />
          {position && (
            <>
              <Marker
                position={position}
                icon={pointIcon}
                draggable
                eventHandlers={{
                  dragend: (e) => { const ll = e.target.getLatLng(); onPick(ll.lat, ll.lng); },
                }}
                data-testid="point-map-marker"
              />
              <Circle center={position} radius={radius} pathOptions={{ color: "#BF3437", fillColor: "#BF3437", fillOpacity: 0.12 }} />
            </>
          )}
        </MapContainer>
      </div>
      <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }}>
        El círculo muestra el radio de tolerancia ({radius} m) dentro del cual se podrá marcar el punto.
      </div>
    </div>
  );
}
